/** Ajustes do Felipe pela tela (13/10/2026): participação das lojas na meta e recebimento dos containers.
 * Validação final no serviço (core/ajustes.py); aqui só o formulário. Tudo entra no Log de decisões. */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import { ajustarContainer, ajustarParticipacao } from './fonte'
import { dataBr, num } from './logic'
import type { NatalData } from './types'

const pctTxt = (v: number) => (Math.round(v * 1000) / 10).toString().replace('.', ',')
const lerPct = (t: string) => Number(t.replace(',', '.'))

export function EditarParticipacao({ d, aberto, onFechar, onSalvo }: { d: NatalData; aberto: boolean; onFechar: () => void; onSalvo: (m: string) => void }) {
  const editaveis = useMemo(() => d.lojas.filter((l) => !l.meta_com), [d])
  const [vals, setVals] = useState<Record<string, string>>({})
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  useEffect(() => {
    if (!aberto) return
    setVals(Object.fromEntries(editaveis.map((l) => [l.store_key, pctTxt(d.premissas.meta_participacao_loja[l.store_key] || 0)])))
    setMotivo(''); setErro(null)
  }, [aberto, editaveis, d])
  const numeros = editaveis.map((l) => lerPct(vals[l.store_key] ?? ''))
  const invalido = numeros.some((v) => !Number.isFinite(v) || v < 0 || v > 100)
  const soma = numeros.reduce((a, v) => a + (Number.isFinite(v) ? v : 0), 0)
  const fecha = Math.abs(soma - 100) < 0.05
  const salvar = async () => {
    setEnviando(true); setErro(null)
    try {
      await ajustarParticipacao(Object.fromEntries(editaveis.map((l, i) => [l.store_key, Math.round(numeros[i] * 10) / 1000])), motivo.trim())
      onSalvo('Participação salva e registrada no Log de decisões. O painel está recalculando as metas (alguns minutos).')
      onFechar()
    } catch (e) { setErro(e instanceof Error ? e.message : String(e)) } finally { setEnviando(false) }
  }
  return (
    <Dialog open={aberto} onClose={() => !enviando && onFechar()} maxWidth="xs" fullWidth>
      <DialogTitle>Participação das lojas na meta</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {editaveis.map((l) => (
            <TextField key={l.store_key} size="small" label={l.nome} value={vals[l.store_key] ?? ''}
              onChange={(e) => setVals({ ...vals, [l.store_key]: e.target.value })}
              slotProps={{ htmlInput: { inputMode: 'decimal' }, input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }} />
          ))}
          {d.lojas.filter((l) => l.meta_com).map((l) => (
            <Typography key={l.store_key} variant="caption" color="text.secondary">{l.nome}: meta com {d.lojas.find((x) => x.store_key === l.meta_com)?.nome ?? l.meta_com} (não editável)</Typography>
          ))}
          <Typography variant="body2" color={fecha && !invalido ? 'success.main' : 'error.main'}>Soma: {soma.toFixed(1).replace('.', ',')}% {fecha ? '' : '(precisa dar 100%)'}</Typography>
          <TextField size="small" label="Motivo (obrigatório)" value={motivo} onChange={(e) => setMotivo(e.target.value)} multiline minRows={2} />
          {erro && <Alert severity="error">{erro}</Alert>}
          <Alert severity="info">A meta de cada loja, o ritmo e o status dos canais passam a usar a nova participação. Nada muda nos marketplaces.</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={() => void salvar()} disabled={enviando || invalido || !fecha || !motivo.trim()}>{enviando ? 'Salvando…' : 'Salvar'}</Button>
      </DialogActions>
    </Dialog>
  )
}

export type Chegada = NatalData['chegadas'][number]

export function AjustarContainer({ d, c, onFechar, onSalvo }: { d: NatalData; c: Chegada | null; onFechar: () => void; onSalvo: (m: string) => void }) {
  const [situacao, setSituacao] = useState<'recebido' | 'previsto'>('recebido')
  const [data, setData] = useState('')
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  useEffect(() => {
    if (!c) return
    setSituacao('recebido'); setData(d.hoje); setMotivo(''); setErro(null)
  }, [c, d.hoje])
  const valida = situacao === 'recebido' ? !!data && data <= d.hoje : !!data && data > d.hoje
  const salvar = async () => {
    if (!c) return
    setEnviando(true); setErro(null)
    try {
      await ajustarContainer(c.embarque, situacao, data, motivo.trim())
      onSalvo(`${c.embarque}: ${situacao === 'recebido' ? `recebido em ${dataBr(data)}` : `nova previsão ${dataBr(data)}`}. Registrado no Log de decisões; o painel está recalculando (alguns minutos).`)
      onFechar()
    } catch (e) { setErro(e instanceof Error ? e.message : String(e)) } finally { setEnviando(false) }
  }
  return (
    <Dialog open={!!c} onClose={() => !enviando && onFechar()} maxWidth="xs" fullWidth>
      <DialogTitle>Container {c?.embarque}</DialogTitle>
      <DialogContent>
        {c && (
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2">{num(c.pecas)} peças · previsto para {dataBr(c.data_informada ?? c.data)}</Typography>
            <ToggleButtonGroup size="small" exclusive value={situacao} onChange={(_, v: 'recebido' | 'previsto' | null) => {
              if (!v) return
              setSituacao(v)
              const amanha = new Date(`${d.hoje}T12:00:00`); amanha.setDate(amanha.getDate() + 1)
              setData(v === 'recebido' ? d.hoje : amanha.toISOString().slice(0, 10))
            }}>
              <ToggleButton value="recebido">Confirmar recebimento</ToggleButton>
              <ToggleButton value="previsto">Nova previsão</ToggleButton>
            </ToggleButtonGroup>
            <TextField size="small" type="date" label={situacao === 'recebido' ? 'Entrou no estoque em' : 'Nova data de chegada'} value={data}
              onChange={(e) => setData(e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: situacao === 'recebido' ? { max: d.hoje } : { min: d.hoje } }}
              helperText={valida ? ' ' : situacao === 'recebido' ? 'não pode ser no futuro' : 'tem que ser depois de hoje'} error={!valida} />
            <TextField size="small" label="Motivo (obrigatório)" value={motivo} onChange={(e) => setMotivo(e.target.value)} multiline minRows={2} />
            {erro && <Alert severity="error">{erro}</Alert>}
            <Alert severity="info">{situacao === 'recebido'
              ? 'A meta dos SKUs deste container passa a contar a partir desta data.'
              : 'Até a nova data, a meta deste container não começa e as peças seguem em "a chegar".'}</Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={() => void salvar()} disabled={enviando || !valida || !motivo.trim()}>{enviando ? 'Salvando…' : 'Salvar'}</Button>
      </DialogActions>
    </Dialog>
  )
}
